import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const AppConfigContext = createContext(null);

const DEFAULT_CONFIG = {
  disabled: false,
  message: '',
  endsAt: null,
  initiatedBy: '',
  adminEmails: [],
  banner: {
    enabled: false,
    text: '',
    tone: 'info',
    linkUrl: '',
    linkLabel: '',
  },
};

export const AppConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState(null);

  useEffect(() => {
    setLoadingConfig(true);
    setConfigError(null);

    const ref = doc(db, 'config', 'app');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() || {};
        const adminEmails = Array.isArray(data.adminEmails)
          ? data.adminEmails.filter((e) => typeof e === 'string').map((e) => e.trim()).filter(Boolean)
          : [];
        setConfig({
          disabled: !!data.disabled,
          message: typeof data.message === 'string' ? data.message : '',
          endsAt: data.endsAt || null,
          updatedAt: data.updatedAt || null,
          updatedBy: typeof data.updatedBy === 'string' ? data.updatedBy : '',
          initiatedBy: typeof data.initiatedBy === 'string' ? data.initiatedBy : '',
          adminEmails,
          banner: {
            enabled: !!data?.banner?.enabled,
            text: typeof data?.banner?.text === 'string' ? data.banner.text : '',
            tone: typeof data?.banner?.tone === 'string' ? data.banner.tone : 'info',
            linkUrl: typeof data?.banner?.linkUrl === 'string' ? data.banner.linkUrl : '',
            linkLabel: typeof data?.banner?.linkLabel === 'string' ? data.banner.linkLabel : '',
          },
        });
        setLoadingConfig(false);
      },
      (err) => {
        setConfigError(err);
        setConfig(DEFAULT_CONFIG);
        setLoadingConfig(false);
      }
    );

    return () => unsub();
  }, []);

  const value = useMemo(
    () => ({
      ...config,
      loadingConfig,
      configError,
    }),
    [config, loadingConfig, configError]
  );

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
};

export const useAppConfig = () => {
  const ctx = useContext(AppConfigContext);
  if (!ctx) throw new Error('useAppConfig must be used within an AppConfigProvider');
  return ctx;
};
